import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/apiClient";
import { duplicateNameFromError } from "../api/duplicateName";
import type { ExistingItemRef } from "../api/types";
import { DuplicateNameDialog } from "../components/DuplicateNameDialog";
import type {
  CatalogDraft,
  CatalogDraftGroup,
  Color,
  DefaultUom,
  ItemCategory,
  SubCategory,
} from "../api/types";
import { PhotoInput } from "../components/PhotoInput";
import { DraftThumb } from "../components/catalog/DraftThumb";
import { BarcodeField } from "../components/BarcodeField";
import { DraftReviewTable } from "../components/catalog/DraftReviewTable";
import { ItemsOnlyUploadBox, type PaperRead } from "../components/catalog/ItemsOnlyUploadBox";
import { AutoCreatedItemsBlock } from "../components/catalog/AutoCreatedItemsBlock";
import { paperReadNotice } from "../utils/paperReadNotice";
import { useAuth } from "../state/AuthContext";
import { isVesselItemCategory } from "../utils/vesselItemCategory";
import { MarginMultiplierHint } from "../components/MarginMultiplierHint";
import { marginMultiplierLabel } from "../utils/marginMultiplier";

const UOMS: DefaultUom[] = ["UNIT", "STEM", "CM", "ROLL"];

/** Local editable mirror of a draft's fields. */
interface DraftEdit {
  name: string;
  producer: string;
  supplierSku: string;
  barcodeRaw: string;
  categoryId: string;
  defaultUom: DefaultUom | "";
  colorId: string;
  stemHeightCm: string;
  /** G-45 follow-up: optional retail price; blank means "derive it from cost". */
  defaultSaleUnitPriceEur: string;
  diameterCm: string;
  depthCm: string;
  subCategoryId: string;
  isPerishable: boolean;
  draftGroupId: string;
}

function toEdit(d: CatalogDraft): DraftEdit {
  return {
    name: d.name ?? "",
    producer: d.producer ?? "",
    supplierSku: d.supplierSku ?? "",
    barcodeRaw: d.barcodeRaw ?? "",
    categoryId: d.categoryId ?? "",
    defaultUom: d.defaultUom ?? "",
    colorId: d.colorId ?? "",
    stemHeightCm: d.stemHeightCm ?? "",
    defaultSaleUnitPriceEur: d.defaultSaleUnitPriceEur ?? "",
    diameterCm: d.diameterCm ?? "",
    depthCm: d.depthCm ?? "",
    subCategoryId: d.subCategoryId ?? "",
    isPerishable: d.isPerishable,
    draftGroupId: d.draftGroupId ?? "",
  };
}

function numOrNull(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > 10_000) {
    throw new Error("Numbers must be between 0 and 10000");
  }
  return n;
}

export function CatalogDraftsPage() {
  const navigate = useNavigate();

  const [drafts, setDrafts] = useState<CatalogDraft[]>([]);
  const [groups, setGroups] = useState<CatalogDraftGroup[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);

  // Per-draft editable mirror, keyed by draft id.
  const [edits, setEdits] = useState<Record<string, DraftEdit>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // G-129: bumped when a paper was read or an automatic creation undone, so
  // the "Created automatically" list follows the queue.
  const [autoCreatedTick, setAutoCreatedTick] = useState(0);
  // G-58: the review pass is the default whenever the parser had something
  // to suggest (Deco); other suppliers land on the detailed cards.
  const [view, setView] = useState<"review" | "detailed" | null>(null);

  // Toolbar inline-create state.
  const [newGroupName, setNewGroupName] = useState("");
  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  // G-30: every new category needs a VAT rate; 21 is the general default.
  const [newCategoryVat, setNewCategoryVat] = useState("21");
  const [newCategoryTargetMargin, setNewCategoryTargetMargin] = useState("50");
  const { hasRole } = useAuth();
  // G-30: creating a category sets tax, so the creator is owner-only
  // (the backend enforces the same).
  const canCreateCategory = hasRole("OWNER");

  const setDraftBusy = useCallback((id: string, on: boolean) => {
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ws = await api.listCatalogDrafts();
      setDrafts(ws.drafts);
      setGroups(ws.groups);
      setView((current) =>
        current ??
        (ws.drafts.some((d) => d.suggestedCategoryCode || d.supplierThumbnailKey)
          ? "review"
          : "detailed"),
      );
      setEdits((prev) => {
        const next: Record<string, DraftEdit> = {};
        for (const d of ws.drafts) {
          // Preserve unsaved local edits where possible; otherwise seed from server.
          next[d.id] = prev[d.id] ?? toEdit(d);
        }
        return next;
      });
      setSelected((prev) => {
        const ids = new Set(ws.drafts.map((d) => d.id));
        return new Set([...prev].filter((id) => ids.has(id)));
      });
      return ws;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load catalog staging");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * G-129 (OD-160): a paper the box above uploaded has been read. The queue
   * reloads, the "Created automatically" list follows, and the notice says
   * what the app did and what is left for a person.
   */
  const handlePaperRead = useCallback(
    async (read: PaperRead) => {
      setError(null);
      // One workspace read: the queue reloads from it and the notice counts from it.
      const ws = await loadWorkspace();
      setAutoCreatedTick((t) => t + 1);
      try {
        const autoCreated = await api.listAutoCreatedItems({ days: 30 });
        const mine = ws?.drafts.filter((d) => d.sourceInvoiceId === read.invoiceId) ?? [];
        setNotice(
          paperReadNotice({
            supplierName: read.supplierName,
            label: read.label,
            created: autoCreated.filter((r) => r.paper?.id === read.invoiceId).length,
            needCategory: mine.filter((d) => d.nameTwins.length === 0).length,
            sameName: mine.filter((d) => d.nameTwins.length > 0).length,
          }),
        );
      } catch {
        setNotice(`Read ${read.supplierName} ${read.label}.`);
      }
    },
    [loadWorkspace],
  );

  const loadCategories = useCallback(async () => {
    try {
      setCategories(await api.listCategories());
    } catch {
      setCategories([]);
    }
  }, []);

  const loadColors = useCallback(async () => {
    try {
      setColors(await api.listColors());
    } catch {
      setColors([]);
    }
  }, []);

  const loadSubCategories = useCallback(async () => {
    try {
      setSubCategories(await api.listSubCategories());
    } catch {
      setSubCategories([]);
    }
  }, []);

  useEffect(() => {
    void loadWorkspace();
    void loadCategories();
    void loadColors();
    void loadSubCategories();
  }, [loadWorkspace, loadCategories, loadColors, loadSubCategories]);

  const patchEdit = useCallback((id: string, patch: Partial<DraftEdit>) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  const categoryCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) map.set(c.id, c.code);
    return map;
  }, [categories]);

  const buildPayload = useCallback(
    (edit: DraftEdit) => {
      const vessel =
        edit.categoryId !== "" &&
        isVesselItemCategory(categoryCodeById.get(edit.categoryId) ?? "");
      return {
        name: edit.name.trim(),
        producer: edit.producer.trim() || null,
        supplierSku: edit.supplierSku.trim() || null,
        barcodeRaw: edit.barcodeRaw.trim() || null,
        categoryId: edit.categoryId || null,
        defaultUom: edit.defaultUom || null,
        colorId: edit.colorId || null,
        stemHeightCm: numOrNull(edit.stemHeightCm),
        defaultSaleUnitPriceEur: numOrNull(edit.defaultSaleUnitPriceEur),
        diameterCm: vessel ? numOrNull(edit.diameterCm) : null,
        depthCm: vessel ? numOrNull(edit.depthCm) : null,
        subCategoryId: edit.subCategoryId || null,
        isPerishable: edit.isPerishable,
        draftGroupId: edit.draftGroupId || null,
      };
    },
    [categoryCodeById],
  );

  const handleSave = useCallback(
    async (draft: CatalogDraft) => {
      const edit = edits[draft.id];
      if (!edit) return;
      setDraftBusy(draft.id, true);
      setError(null);
      setNotice(null);
      try {
        const updated = await api.updateCatalogDraft(draft.id, buildPayload(edit));
        setDrafts((prev) => prev.map((d) => (d.id === draft.id ? updated : d)));
        setNotice(`Saved "${updated.name}".`);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not save draft");
      } finally {
        setDraftBusy(draft.id, false);
      }
    },
    [edits, buildPayload, setDraftBusy],
  );

  const handlePhoto = useCallback(
    async (draftId: string, file: File | null) => {
      if (!file) return;
      setDraftBusy(draftId, true);
      setError(null);
      try {
        const updated = await api.setCatalogDraftPhoto(draftId, file);
        setDrafts((prev) => prev.map((d) => (d.id === draftId ? updated : d)));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not upload photo");
      } finally {
        setDraftBusy(draftId, false);
      }
    },
    [setDraftBusy],
  );

  // G-119: a promote the server held back because an active item already
  // carries the draft's name — one draft (the dialog) or a pass (the notice
  // with "Promote anyway").
  const [duplicatePrompt, setDuplicatePrompt] = useState<{
    draft: CatalogDraft;
    existing: ExistingItemRef[];
  } | null>(null);
  const [duplicateBatch, setDuplicateBatch] = useState<{
    targets: CatalogDraft[];
    names: string[];
    twinIds: string[];
  } | null>(null);

  const handleFinalize = useCallback(
    async (draft: CatalogDraft, confirmDuplicateOf?: string[]) => {
      const edit = edits[draft.id];
      if (!edit) return;
      if (!edit.categoryId || !edit.defaultUom) {
        setError("A category and a unit of measure are required before finalizing.");
        return;
      }
      setDraftBusy(draft.id, true);
      setError(null);
      setNotice(null);
      try {
        // Persist current edits first — finalize reads the stored draft.
        await api.updateCatalogDraft(draft.id, buildPayload(edit));
        const item = await api.finalizeCatalogDraft(draft.id, {
          ...(confirmDuplicateOf?.length ? { confirmDuplicateOf } : {}),
        });
        setDuplicatePrompt(null);
        // Remove the now-finalized draft from the workspace view.
        setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(draft.id);
          return next;
        });
        setNotice(`Added "${item.name}" to the catalog.`);
        // Refresh groups so newly created group items reflect their state.
        void loadWorkspace();
      } catch (err) {
        const twins = duplicateNameFromError(err);
        if (twins) {
          // Shown (or shown again, if a twin appeared meanwhile); the dialog
          // stays until the person answers.
          setDuplicatePrompt({ draft, existing: twins });
          return;
        }
        setDuplicatePrompt(null);
        setError(err instanceof Error ? err.message : "Could not finalize draft");
      } finally {
        setDraftBusy(draft.id, false);
      }
    },
    [edits, buildPayload, setDraftBusy, loadWorkspace],
  );

  // --- G-58 review pass ----------------------------------------------------

  /** Inline edit in the review table: a partial save, mirrored into the card state. */
  const handleReviewPatch = useCallback(
    async (
      draft: CatalogDraft,
      patch: {
        name?: string;
        categoryId?: string | null;
        colorId?: string | null;
        barcodeRaw?: string | null;
      },
    ) => {
      setDraftBusy(draft.id, true);
      setError(null);
      try {
        const updated = await api.updateCatalogDraft(draft.id, patch);
        setDrafts((prev) => prev.map((d) => (d.id === draft.id ? updated : d)));
        setEdits((prev) => ({ ...prev, [draft.id]: toEdit(updated) }));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not save draft");
      } finally {
        setDraftBusy(draft.id, false);
      }
    },
    [setDraftBusy],
  );

  /**
   * Promote through the same server path for one row and for the whole
   * block, so a single row never behaves differently from the batch.
   */
  const promoteMany = useCallback(
    async (targets: CatalogDraft[], confirmDuplicateOf?: string[]) => {
      if (targets.length === 0) return;
      const ids = targets.map((d) => d.id);
      for (const id of ids) setDraftBusy(id, true);
      setError(null);
      setNotice(null);
      setDuplicateBatch(null);
      try {
        const result = await api.finalizeCatalogDraftsMany({
          draftIds: ids,
          applySuggestions: true,
          ...(confirmDuplicateOf?.length ? { confirmDuplicateOf } : {}),
        });
        const promotedIds = new Set(
          result.results.filter((r) => r.itemId).map((r) => r.draftId),
        );
        setDrafts((prev) => prev.filter((d) => !promotedIds.has(d.id)));
        setSelected((prev) => new Set([...prev].filter((id) => !promotedIds.has(id))));
        // G-119: drafts held back because an active item already carries
        // their name are offered again, together, as "promote anyway".
        const heldBack = result.results.filter((r) => r.duplicateOf && r.duplicateOf.length > 0);
        if (heldBack.length > 0) {
          const heldIds = new Set(heldBack.map((r) => r.draftId));
          setDuplicateBatch({
            targets: targets.filter((d) => heldIds.has(d.id)),
            names: heldBack.map((r) => r.duplicateOf?.[0]?.name ?? "").filter(Boolean),
            // The twins SHOWN — what "promote anyway" confirms, and only those.
            twinIds: [...new Set(heldBack.flatMap((r) => (r.duplicateOf ?? []).map((e) => e.id)))],
          });
        }
        const failures = result.results.filter((r) => r.error && !r.duplicateOf);
        if (failures.length > 0) {
          const nameOf = (id: string) =>
            targets.find((d) => d.id === id)?.supplierSku ??
            targets.find((d) => d.id === id)?.name ??
            id;
          setError(
            `${failures.length} not promoted: ${failures
              .slice(0, 5)
              .map((f) => `${nameOf(f.draftId)} — ${f.error}`)
              .join("; ")}${failures.length > 5 ? "; …" : ""}`,
          );
        }
        setNotice(
          result.promoted === 1 && targets.length === 1
            ? `Added "${targets[0].name}" to the catalog.`
            : `Promoted ${result.promoted} of ${targets.length} items to the catalog.`,
        );
        void loadWorkspace();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not promote drafts");
      } finally {
        for (const id of ids) setDraftBusy(id, false);
      }
    },
    [setDraftBusy, loadWorkspace],
  );

  const handleDelete = useCallback(
    async (draft: CatalogDraft) => {
      if (!window.confirm(`Delete draft "${draft.name}"? This cannot be undone.`)) return;
      setDraftBusy(draft.id, true);
      setError(null);
      try {
        await api.deleteCatalogDraft(draft.id);
        setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(draft.id);
          return next;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not delete draft");
      } finally {
        setDraftBusy(draft.id, false);
      }
    },
    [setDraftBusy],
  );

  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleGroupSelected = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name) {
      setError("Enter a name for the new group.");
      return;
    }
    setError(null);
    setNotice(null);
    try {
      const draftIds = [...selected];
      await api.createCatalogDraftGroup({ name, draftIds });
      setNewGroupName("");
      setSelected(new Set());
      await loadWorkspace();
      setNotice(
        draftIds.length > 0
          ? `Created group "${name}" with ${draftIds.length} item(s).`
          : `Created group "${name}".`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create group");
    }
  }, [newGroupName, selected, loadWorkspace]);

  const handleDeleteGroup = useCallback(
    async (group: CatalogDraftGroup) => {
      if (
        !window.confirm(
          `Delete group "${group.name}"? Its drafts will be ungrouped (not deleted).`,
        )
      )
        return;
      setError(null);
      try {
        await api.deleteCatalogDraftGroup(group.id);
        await loadWorkspace();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not delete group");
      }
    },
    [loadWorkspace],
  );

  const handleCreateCategory = useCallback(async () => {
    const label = newCategoryLabel.trim();
    if (!label) return;
    const vatRate = Number(newCategoryVat);
    if (!newCategoryVat.trim() || !Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
      setError("Enter a VAT rate between 0 and 100 for the new category");
      return;
    }
    // G-45: bounded BELOW 100 — the price formula divides by (1 - m/100).
    const targetMargin = Number(newCategoryTargetMargin);
    if (
      !newCategoryTargetMargin.trim() ||
      !Number.isFinite(targetMargin) ||
      targetMargin < 0 ||
      targetMargin >= 100
    ) {
      setError("Enter a target margin of 0 or more and under 100");
      return;
    }
    setError(null);
    try {
      await api.createCategory({
        label,
        vatRatePercent: vatRate,
        targetMarginPercent: targetMargin,
      });
      setNewCategoryLabel("");
      setNewCategoryVat("21");
      setNewCategoryTargetMargin("50");
      await loadCategories();
      const multiplier = marginMultiplierLabel(targetMargin, vatRate);
      setNotice(
        `Category "${label}" created (${vatRate}% VAT, ${targetMargin}% target margin${
          multiplier ? ` · ${multiplier}` : ""
        }).`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create category");
    }
  }, [newCategoryLabel, newCategoryVat, newCategoryTargetMargin, loadCategories]);

  // Group pending drafts by supplier for display.
  const draftsBySupplier = useMemo(() => {
    const map = new Map<string, { name: string; drafts: CatalogDraft[] }>();
    for (const d of drafts) {
      const key = d.supplierId;
      if (!map.has(key)) map.set(key, { name: d.supplier?.name ?? "Unknown supplier", drafts: [] });
      map.get(key)!.drafts.push(d);
    }
    return [...map.entries()].map(([id, v]) => ({ id, ...v }));
  }, [drafts]);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Catalog staging</h2>
          <p className="page-subtitle">
            Review items extracted from supplier invoices. Add a photo, set the category, unit and
            details, optionally group variants together, then finalize each item to add it to the
            catalog.
          </p>
        </div>
      </div>

      <ItemsOnlyUploadBox onRead={handlePaperRead} />

      {error ? <div className="alert error full">{error}</div> : null}
      {notice ? <div className="alert success full">{notice}</div> : null}
      {duplicateBatch ? (
        <div className="alert warning" style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", alignItems: "center" }}>
          <span>
            {duplicateBatch.targets.length === 1
              ? `1 draft was not promoted: an item called "${duplicateBatch.names[0] ?? ""}" already exists.`
              : `${duplicateBatch.targets.length} drafts were not promoted: items with the same names already exist (${duplicateBatch.names.slice(0, 3).join(", ")}${duplicateBatch.names.length > 3 ? ", …" : ""}).`}{" "}
            Pick the existing items, or promote these anyway if they are really different products.
          </span>
          <button
            type="button"
            className="button danger"
            onClick={() => void promoteMany(duplicateBatch.targets, duplicateBatch.twinIds)}
          >
            Promote {duplicateBatch.targets.length === 1 ? "it" : `all ${duplicateBatch.targets.length}`} anyway
          </button>
          <button type="button" className="button plain" onClick={() => setDuplicateBatch(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {duplicatePrompt ? (
        <DuplicateNameDialog
          name={duplicatePrompt.draft.name}
          existing={duplicatePrompt.existing}
          confirmLabel="Promote it anyway"
          busy={busy.has(duplicatePrompt.draft.id)}
          onConfirm={() =>
            void handleFinalize(
              duplicatePrompt.draft,
              duplicatePrompt.existing.map((e) => e.id),
            )
          }
          onClose={() => setDuplicatePrompt(null)}
        />
      ) : null}

      {/* Toolbar: bulk-group + inline create category / color */}
      <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", alignItems: "flex-end" }}>
          <div className="field" style={{ flex: "1 1 220px" }}>
            <label htmlFor="newGroupName">Group selected as…</label>
            <input
              id="newGroupName"
              type="text"
              placeholder="e.g. Tulips"
              maxLength={120}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
            <p className="field-hint">{selected.size} draft(s) selected.</p>
          </div>
          <button
            type="button"
            className="button"
            onClick={() => void handleGroupSelected()}
            disabled={!newGroupName.trim()}
          >
            Create group
          </button>
        </div>

        {canCreateCategory ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", alignItems: "flex-end" }}>
            <div className="field" style={{ flex: "1 1 180px" }}>
              <label htmlFor="newCategory">New category</label>
              <input
                id="newCategory"
                type="text"
                placeholder="e.g. Candles"
                maxLength={120}
                value={newCategoryLabel}
                onChange={(e) => setNewCategoryLabel(e.target.value)}
                aria-describedby="newCategoryHint"
              />
              <p id="newCategoryHint" className="field-hint">
                A real catalogue category (not a staging group): VAT % for every item in it ·
                target margin % (a share of the price before IVA)
              </p>
            </div>
            <div className="field" style={{ flex: "0 1 110px" }}>
              <label htmlFor="newCategoryVat">VAT %</label>
              <input
                id="newCategoryVat"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step={0.01}
                value={newCategoryVat}
                onChange={(e) => setNewCategoryVat(e.target.value)}
              />
            </div>
            <div className="field" style={{ flex: "0 1 140px" }}>
              <label htmlFor="newCategoryTargetMargin">Target margin %</label>
              <input
                id="newCategoryTargetMargin"
                type="number"
                inputMode="decimal"
                min={0}
                max={99.99}
                step={0.01}
                value={newCategoryTargetMargin}
                onChange={(e) => setNewCategoryTargetMargin(e.target.value)}
              />
              <MarginMultiplierHint
                targetMarginPercent={newCategoryTargetMargin}
                vatRatePercent={newCategoryVat}
              />
            </div>
            <button
              type="button"
              className="button plain"
              onClick={() => void handleCreateCategory()}
              disabled={!newCategoryLabel.trim()}
            >
              Add category
            </button>
          </div>
        ) : null}
      </div>

      {/* Existing draft groups */}
      {groups.length > 0 ? (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Groups</h3>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {groups.map((g) => (
              <li
                key={g.id}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-2)" }}
              >
                <span>
                  <strong>{g.name}</strong>{" "}
                  <span className="field-hint">
                    ({g._count.drafts} item{g._count.drafts === 1 ? "" : "s"}
                    {g.category ? ` · ${g.category.label}` : ""})
                  </span>
                </span>
                <button
                  type="button"
                  className="button danger"
                  onClick={() => void handleDeleteGroup(g)}
                >
                  Delete group
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {loading ? (
        <div className="card">Loading…</div>
      ) : drafts.length === 0 ? (
        <div className="card">
          <p>
            Nothing waiting. Read a paper above, or upload a delivery paper from Deliveries — when a
            paper is read, the app creates the items it can by itself and lists the rest here.
          </p>
          <p className="muted" style={{ fontSize: 13 }}>
            Items created here belong to the whole business, not to one shop.
          </p>
          <div className="button-row" style={{ gap: "var(--space-2)" }}>
            <button type="button" className="button plain" onClick={() => navigate("/app/deliveries")}>
              Go to deliveries
            </button>
          </div>
        </div>
      ) : (
        draftsBySupplier.map((supplier) => (
          <div key={supplier.id} style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0 }}>
                {supplier.name}{" "}
                <span className="field-hint">({supplier.drafts.length} pending)</span>
              </h3>
              <div className="view-switch" role="group" aria-label="How to review drafts">
                <button
                  type="button"
                  className="button plain"
                  aria-pressed={view === "review"}
                  onClick={() => setView("review")}
                >
                  Review pass
                </button>
                <button
                  type="button"
                  className="button plain"
                  aria-pressed={view === "detailed"}
                  onClick={() => setView("detailed")}
                >
                  Detailed cards
                </button>
              </div>
            </div>
            {view === "review" ? (
              <div className="card">
                <DraftReviewTable
                  drafts={supplier.drafts}
                  categories={categories}
                  colors={colors}
                  busyIds={busy}
                  onPatch={handleReviewPatch}
                  onPhoto={handlePhoto}
                  onPromoteOne={(draft) => promoteMany([draft])}
                  onPromoteAll={promoteMany}
                  onDelete={handleDelete}
                />
              </div>
            ) : null}
            {view === "detailed" ? supplier.drafts.map((draft) => {
              const edit = edits[draft.id];
              if (!edit) return null;
              const isBusy = busy.has(draft.id);
              const code = categoryCodeById.get(edit.categoryId) ?? "";
              const showVessel = code !== "" && isVesselItemCategory(code);
              const canFinalize = Boolean(edit.categoryId) && Boolean(edit.defaultUom);
              return (
                <div className="card" key={draft.id} style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start" }}>
                    <label style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={selected.has(draft.id)}
                        onChange={() => toggleSelected(draft.id)}
                        aria-label={`Select ${draft.name}`}
                      />
                    </label>
                    <DraftThumb draft={draft} />
                    <div style={{ flex: 1 }}>
                      <p className="field-hint" style={{ margin: 0 }}>
                        From invoice: {draft.sourceInvoice?.invoiceNumber ?? "—"}
                        {draft.supplierSku ? ` · Ref ${draft.supplierSku}` : ""}
                      </p>
                      <p className="field-hint" style={{ margin: 0 }}>
                        Original: {draft.nameRaw}
                        {draft.group ? ` · Group: ${draft.group.name}` : ""}
                      </p>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="field full">
                      <label htmlFor={`name-${draft.id}`}>Name *</label>
                      <input
                        id={`name-${draft.id}`}
                        type="text"
                        maxLength={200}
                        value={edit.name}
                        onChange={(e) => patchEdit(draft.id, { name: e.target.value })}
                      />
                    </div>

                    <div className="field">
                      <label htmlFor={`cat-${draft.id}`}>Category *</label>
                      <select
                        id={`cat-${draft.id}`}
                        value={edit.categoryId}
                        onChange={(e) =>
                          patchEdit(draft.id, { categoryId: e.target.value, subCategoryId: "" })
                        }
                      >
                        <option value="">Select a category</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label htmlFor={`uom-${draft.id}`}>Unit of measure *</label>
                      <select
                        id={`uom-${draft.id}`}
                        value={edit.defaultUom}
                        onChange={(e) =>
                          patchEdit(draft.id, { defaultUom: e.target.value as DefaultUom | "" })
                        }
                      >
                        <option value="">Select a unit</option>
                        {UOMS.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label htmlFor={`color-${draft.id}`}>Color</label>
                      <select
                        id={`color-${draft.id}`}
                        value={edit.colorId}
                        onChange={(e) => patchEdit(draft.id, { colorId: e.target.value })}
                      >
                        <option value="">— None —</option>
                        {colors.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label htmlFor={`group-${draft.id}`}>Group</label>
                      <select
                        id={`group-${draft.id}`}
                        value={edit.draftGroupId}
                        onChange={(e) => patchEdit(draft.id, { draftGroupId: e.target.value })}
                      >
                        <option value="">— Ungrouped —</option>
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label htmlFor={`stem-${draft.id}`}>Stem height (cm)</label>
                      <input
                        id={`stem-${draft.id}`}
                        type="number"
                        min={0}
                        max={10_000}
                        step="any"
                        value={edit.stemHeightCm}
                        onChange={(e) => patchEdit(draft.id, { stemHeightCm: e.target.value })}
                      />
                    </div>

                    {/* G-45 follow-up: OPTIONAL on purpose. Staging promotes a
                        whole delivery note at once, so requiring a price per row
                        would be the wrong trade (G-13 made the purchase cost
                        optional for the same reason). Left blank, the price is
                        derived from real batch cost once the goods arrive. */}
                    <div className="field">
                      <label htmlFor={`price-${draft.id}`}>Selling price (€)</label>
                      <input
                        id={`price-${draft.id}`}
                        type="number"
                        min={0}
                        step="0.01"
                        inputMode="decimal"
                        placeholder="Leave blank to work it out from cost"
                        value={edit.defaultSaleUnitPriceEur}
                        onChange={(e) =>
                          patchEdit(draft.id, { defaultSaleUnitPriceEur: e.target.value })
                        }
                        aria-describedby={`price-hint-${draft.id}`}
                      />
                      <p id={`price-hint-${draft.id}`} className="field-hint">
                        Optional · blank = the suggested price (cost, target margin, IVA), following
                        supplier prices
                      </p>
                    </div>

                    {showVessel ? (
                      <>
                        <div className="field">
                          <label htmlFor={`dia-${draft.id}`}>Diameter (cm)</label>
                          <input
                            id={`dia-${draft.id}`}
                            type="number"
                            min={0}
                            max={10_000}
                            step="any"
                            value={edit.diameterCm}
                            onChange={(e) => patchEdit(draft.id, { diameterCm: e.target.value })}
                          />
                        </div>
                        <div className="field">
                          <label htmlFor={`depth-${draft.id}`}>Depth (cm)</label>
                          <input
                            id={`depth-${draft.id}`}
                            type="number"
                            min={0}
                            max={10_000}
                            step="any"
                            value={edit.depthCm}
                            onChange={(e) => patchEdit(draft.id, { depthCm: e.target.value })}
                          />
                        </div>
                      </>
                    ) : null}

                    <div className="field">
                      <label htmlFor={`producer-${draft.id}`}>Producer / grower</label>
                      <input
                        id={`producer-${draft.id}`}
                        type="text"
                        maxLength={200}
                        value={edit.producer}
                        onChange={(e) => patchEdit(draft.id, { producer: e.target.value })}
                      />
                      <p className="field-hint">
                        Who grew or made it, as the supplier named it. Carried onto the item.
                      </p>
                    </div>

                    <div className="field">
                      <label htmlFor={`sku-${draft.id}`}>Supplier Ref / SKU</label>
                      <input
                        id={`sku-${draft.id}`}
                        type="text"
                        maxLength={64}
                        value={edit.supplierSku}
                        onChange={(e) => patchEdit(draft.id, { supplierSku: e.target.value })}
                      />
                    </div>

                    <BarcodeField
                      id={`barcode-${draft.id}`}
                      value={edit.barcodeRaw}
                      onChange={(v) => patchEdit(draft.id, { barcodeRaw: v })}
                    />

                    <div className="field full">
                      <label htmlFor={`subcat-${draft.id}`}>Flower type</label>
                      <select
                        id={`subcat-${draft.id}`}
                        value={edit.subCategoryId}
                        onChange={(e) => patchEdit(draft.id, { subCategoryId: e.target.value })}
                      >
                        <option value="">— None —</option>
                        {subCategories
                          .filter((s) => !edit.categoryId || s.categoryId === edit.categoryId)
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.label}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="field full">
                      <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={edit.isPerishable}
                          onChange={(e) => patchEdit(draft.id, { isPerishable: e.target.checked })}
                        />
                        <span>Perishable (flowers / plants)</span>
                      </label>
                    </div>

                    <div className="field full">
                      <label>Photo</label>
                      <PhotoInput value={null} onChange={(file) => void handlePhoto(draft.id, file)} />
                      {draft.imageKey ? (
                        <p className="field-hint">A photo is saved for this draft.</p>
                      ) : null}
                    </div>
                  </div>

                  <div className="button-row" style={{ gap: "var(--space-2)" }}>
                    <button
                      type="button"
                      className="button"
                      onClick={() => void handleFinalize(draft)}
                      disabled={isBusy || !canFinalize || !edit.name.trim()}
                      title={canFinalize ? undefined : "Set a category and unit of measure first"}
                    >
                      {isBusy ? "Working…" : "Finalize and add"}
                    </button>
                    <button
                      type="button"
                      className="button plain"
                      onClick={() => void handleSave(draft)}
                      disabled={isBusy}
                    >
                      Save changes
                    </button>
                    <button
                      type="button"
                      className="button danger"
                      onClick={() => void handleDelete(draft)}
                      disabled={isBusy}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            }) : null}
          </div>
        ))
      )}

      <AutoCreatedItemsBlock
        categories={categories}
        reloadTick={autoCreatedTick}
        onUndone={() => {
          setAutoCreatedTick((t) => t + 1);
          void loadWorkspace();
        }}
      />
    </section>
  );
}
